import _ from 'lodash';
import React, {FC, useState} from 'react';

import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {CompanyModel} from '../../../../models/api/companyModel';
import {InboxModel} from '../../../../models/api/inboxModel';
import {RefinementOptionModel} from '../../../../models/api/refinementOptionModel';
import {TagModel} from '../../../../models/api/tagModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {LensCriterion} from '../../../../store/data/refinements/refinementsSelectors';
import {RefinedInboxParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {CellsRefinementOptionsSearchAssigneesDropdown} from './cellsRefinementOptionsSearchAssigneesDropdown';
import {CellsRefinementOptionsSearchDropdownNavigation} from './cellsRefinementOptionsSearchDropdownNavigation';
import {CellsRefinementOptionsSearchTagsDropdown} from './cellsRefinementOptionsSearchTagsDropdown';

enum SearchDropdownStatesEnum {
  NAVIGATION = 'navigation',
  SEARCH_TAGS = 'search-tags',
  SEARCH_ASSIGNEES = 'search-assignees',
}

/*
 * Props.
 */

interface CellsRefinementOptionsSearchDropdownProps {
  company: CompanyModel;
  tagNamespaces: ReadonlyArray<string>;
  popoverProps: PopoverTransferProps;
  selectedRefinement: RefinementOptionModel | undefined;
  selectedTags: ReadonlyArray<TagModel> | undefined;
  selectedAssignee: TeammateModel | undefined;
  parameters: RefinedInboxParameters;
  onSelectAssignee: HandlerOf<TeammateModel>;
  onSelectInbox: HandlerOf<InboxModel>;
  onSelectTag: HandlerOf<TagModel>;
  lensCriteria: ReadonlyArray<LensCriterion>;
  hasAssigneeLensCriteria: boolean;
  lensUpdatedAt: number | undefined;
}

/*
 * Component.
 */

export const CellsRefinementOptionsSearchDropdown: FC<CellsRefinementOptionsSearchDropdownProps> = (
  props,
) => {
  const {
    company,
    tagNamespaces,
    popoverProps,
    selectedRefinement,
    selectedTags,
    selectedAssignee,
    parameters,
    onSelectAssignee,
    onSelectInbox,
    onSelectTag,
    lensCriteria,
    hasAssigneeLensCriteria,
    lensUpdatedAt,
  } = props;

  // Only display lens criteria when there is more than one criteria per type, i.e., we can further refine
  // the P2 results by selecting a specific criteria.
  const divisibleLensCriteria = computeDivisibleLensCriteria(lensCriteria);

  // If the only option we would render in the search dropdown navigation menu is to navigate to the tags search, we
  // shortcut to tags search so that we save a click.
  const usingTagsSearchShortcut = divisibleLensCriteria.length === 0 && hasAssigneeLensCriteria;
  const defaultDropdownState = usingTagsSearchShortcut
    ? SearchDropdownStatesEnum.SEARCH_TAGS
    : SearchDropdownStatesEnum.NAVIGATION;

  const [dropdownState, setDropdownState] = useState(defaultDropdownState);

  const onDropdownNavigateBack = () => setDropdownState(SearchDropdownStatesEnum.NAVIGATION);

  if (dropdownState === SearchDropdownStatesEnum.SEARCH_ASSIGNEES) {
    return (
      <CellsRefinementOptionsSearchAssigneesDropdown
        company={company}
        popoverProps={popoverProps}
        selectedRefinement={selectedRefinement}
        parameters={parameters}
        onSelectAssignee={onSelectAssignee}
        onDropdownNavigateBack={onDropdownNavigateBack}
      />
    );
  }

  if (dropdownState === SearchDropdownStatesEnum.SEARCH_TAGS) {
    return (
      <CellsRefinementOptionsSearchTagsDropdown
        company={company}
        popoverProps={popoverProps}
        selectedRefinement={selectedRefinement}
        selectedTags={selectedTags}
        parameters={parameters}
        onSelectTag={onSelectTag}
        onDropdownNavigateBack={onDropdownNavigateBack}
        usingTagsSearchShortcut={usingTagsSearchShortcut}
        tagNamespaces={tagNamespaces}
      />
    );
  }

  return (
    <CellsRefinementOptionsSearchDropdownNavigation
      parameters={parameters}
      popoverProps={popoverProps}
      selectedRefinement={selectedRefinement}
      lensCriteria={divisibleLensCriteria}
      selectedAssignee={selectedAssignee}
      onSelectAssignee={onSelectAssignee}
      onNavigateToSearchAssignees={() => setDropdownState(SearchDropdownStatesEnum.SEARCH_ASSIGNEES)}
      onSelectInbox={onSelectInbox}
      selectedTags={selectedTags}
      onSelectTag={onSelectTag}
      onNavigateToSearchTags={() => setDropdownState(SearchDropdownStatesEnum.SEARCH_TAGS)}
      tagNamespaces={tagNamespaces}
      hasAssigneeLensCriteria={hasAssigneeLensCriteria}
      lensUpdatedAt={lensUpdatedAt}
    />
  );
};

function computeDivisibleLensCriteria(lensCriteria: ReadonlyArray<LensCriterion>) {
  const criteriaByType = _.groupBy(lensCriteria, (criterion) => `${criterion.type}`);
  return lensCriteria.filter((criterion) => criteriaByType[`${criterion.type}`]?.length > 1);
}
